import { useSearchParams } from "react-router-dom";
import {
  Page,
  Table,
  type Column,
  Input,
  Select,
  FilterField,
  FilterPanel,
  Pagination,
  Text,
  DateTimeField,
  EmptyState,
} from "@marekl/adminer/components";
import { useDebouncedSearchFilter } from "@marekl/adminer/hooks";
import { useAuditLogList, type AuditLog } from "../../lib/queries/auditLogs";
import { usePagedList } from "../../lib/usePagedList";
import { JsonBlock } from "../../components/JsonBlock";
import { RefreshButton } from "../../components/RefreshButton";
import {
  ACTION_OPTIONS,
  ENTITY_TYPE_OPTIONS,
  labelAction,
  labelEntityType,
} from "./auditLabels";

/** Everything this page shows, for the header's refresh button. */
const REFRESH_KEYS = [["audit-logs"]];

export function AuditLogPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const action = searchParams.get("action") ?? "";
  const entityType = searchParams.get("entity_type") ?? "";
  const q = searchParams.get("q") ?? "";

  const { data, isLoading, error, refetch, pagination, filterReset, summary } =
    usePagedList(
      useAuditLogList,
      {
        action: action || undefined,
        entity_type: entityType || undefined,
        q: q || undefined,
      },
      {
        searchParams,
        setSearchParams,
        nounForms: ["record", "records", "records"],
      },
    );

  function applyFilter(key: string, value: string) {
    filterReset((p) => {
      if (value) {
        p.set(key, value);
      } else {
        p.delete(key);
      }
    });
  }

  const { draft: qDraft, setDraft: setQDraft } = useDebouncedSearchFilter({
    value: q,
    onCommit: (next) => applyFilter("q", next),
  });

  const items = data?.items ?? [];

  const columns: Column<AuditLog>[] = [
    {
      key: "created_at",
      header: "Time",
      render: (row) => (
        <Text variant="mono">
          <DateTimeField record={row} source="created_at" />
        </Text>
      ),
    },
    {
      key: "user",
      header: "User",
      render: (row) => row.username || <Text variant="muted">system</Text>,
    },
    {
      key: "action",
      header: "Action",
      primary: true,
      render: (row) => labelAction(row.action),
    },
    {
      key: "entity",
      header: "Entity",
      render: (row) =>
        row.entity_type ? (
          <span>
            {labelEntityType(row.entity_type)}
            {row.entity_id != null && (
              <>
                {" "}
                <Text variant="mono">{row.entity_id}</Text>
              </>
            )}
          </span>
        ) : (
          <Text variant="muted">—</Text>
        ),
    },
    {
      key: "ip",
      header: "IP",
      render: (row) => <Text variant="mono">{row.ip}</Text>,
    },
    {
      key: "details",
      header: "Details",
      render: (row) =>
        row.details_json ? (
          <JsonBlock json={row.details_json} label="View" />
        ) : (
          <Text variant="muted">—</Text>
        ),
    },
  ];

  return (
    <Page>
      <Page.Header
        title="Audit log"
        subtitle="records cannot be deleted — only browsed and filtered"
        subtitleVariant="tagline"
        actions={<RefreshButton queryKeys={REFRESH_KEYS} />}
      />
      <Page.Content stack gap="lg">
        <FilterPanel>
          <FilterField>
            <Select
              label="Action"
              value={action}
              onChange={(e) => applyFilter("action", e.target.value)}
            >
              {ACTION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FilterField>
          <FilterField>
            <Select
              label="Entity"
              value={entityType}
              onChange={(e) => applyFilter("entity_type", e.target.value)}
            >
              {ENTITY_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FilterField>
          <FilterField wide>
            <Input
              label="Search"
              type="search"
              value={qDraft}
              placeholder="user, IP or details"
              onChange={(e) => setQDraft(e.target.value)}
            />
          </FilterField>
        </FilterPanel>

        <Table<AuditLog>
          striped
          cards
          columns={columns}
          rows={items}
          rowKey={(r) => r.id}
          isLoading={isLoading}
          error={error}
          onRetry={refetch}
          empty={
            <EmptyState
              title="No records"
              description="No records match the selected filters."
            />
          }
        />
      </Page.Content>
      <Page.Footer>
        <Pagination {...pagination} summary={summary} />
      </Page.Footer>
    </Page>
  );
}
